import React, { useEffect } from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { useSelector } from 'react-redux';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { Text } from '@uhg-abyss/web/ui/Text';
import { useForm } from '@uhg-abyss/web/hooks/useForm';

const FullNameText = styled('span', {
  color: '#424242',
  fontSize: '14px',
  lineHeight: '18px',
  fontWeight: 400,
  verticalAlign: '-webkit-baseline-middle',
});
const Wrapper = styled('div', {
  '.abyss-descriptors-error-message': {
    fontSize: '13px ',
    fontWeight: '400',
  },
});
const FlexDisplay = styled('div', {
  display: 'flex',
  gap: '20px 4%',
  alignItems: 'baseline',
  flexWrap: 'wrap',

  '@screen < $sm': {
    gap: '20px 0',
  },
});

const FlexItem = styled('div', {
  flexBasis: '48%',
  textAlign: 'left',
  '@screen < $sm': {
    flexBasis: '100%',
  },
});
const Title = styled('h2', {
  color: 'rgb(0, 38, 119)',
  textAlign: 'left',
  fontSize: '25.63px !important',
  fontWeight: '700',
  minHeight: '32px',
  lineHeight: '32px',
  marginBottom: '5px',
});
export const FirstPart = () => {
  const form = useForm();
  const userInfo = useSelector((state) => {
    return state.GET_USER_INFO.data;
  });

  useEffect(() => {
    form.setValue('orgInfoPartOne.email', userInfo.email);
  }, [userInfo]);

  const onOrgTypeChange = () => {
    form.setValue('orgInfoPartTwo.emrVendor', '');
    form.setValue('orgInfoPartTwo.rcmSupportedServices', []);
    form.setValue('orgInfoPartTwo.automations', '');
  };

  return (
    <Wrapper>
      <Title>Tell us about your organization</Title>
      <FlexDisplay>
        <FlexItem>
          <TextInput
            id="orgName"
            label="Organization Name"
            type="text"
            model="orgInfoPartOne.orgName"
            isClearable
            validators={{ required: true }}
            css={{
              'abyss-text-input-label': {
                position: 'relative',
                fontSize: '15px !important',
              },
            }}
          />
        </FlexItem>

        <FlexItem id="fullName">
          <Text css={{ fontSize: '15px' }}>Full Name</Text>
          <FullNameText>{`${userInfo.firstName} ${
            userInfo.lastName || userInfo.family_name
          }`}</FullNameText>
        </FlexItem>

        <FlexItem>
          <TextInput
            id="orgWebsite"
            label="Organization Website"
            type="text"
            model="orgInfoPartOne.orgWebsite"
            isClearable
            css={{
              'abyss-text-input-label': {
                position: 'relative',
                fontSize: '15px',
              },
            }}
          />
        </FlexItem>
        <FlexItem>
          <TextInput
            id="phone"
            label="Phone Number"
            model="orgInfoPartOne.phone"
            isClearable
            mask="phone"
            validators={{
              required: false,
              pattern: {
                value: /^\(\d{3}\) \d{3}-\d{4}$/,
                message:
                  'Please enter a valid phone number in the format (999) 999-9999',
              },
            }}
            css={{
              'abyss-text-input-label': {
                position: 'relative',
                fontSize: '15px',
              },
            }}
          />
        </FlexItem>

        <FlexItem>
          <TextInput
            id="email"
            label="Email"
            type="text"
            model="orgInfoPartOne.email"
            isClearable
            validators={{
              required: true,
              pattern: {
                // eslint-disable-next-line no-control-regex
                value:
                  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                message: 'Invalid email format.',
              },
            }}
            css={{
              'abyss-text-input-label': {
                position: 'relative',
                fontSize: '15px',
              },
            }}
          />
        </FlexItem>
        <FlexItem>
          <TextInput
            id="primaryTaxId"
            label="What is your primary Tax ID?"
            model="orgInfoPartOne.primaryTaxId"
            maxLength={9}
            isClearable
            type="text"
            mask="numeric"
            pattern="\d*"
            validators={{
              required: true,
              pattern: {
                value: /^\d+$/,
                message: 'Tax ID must contain only numbers',
              },
              minLength: {
                value: 9,
                message: 'Tax ID must be at least 9 characters long',
              },
            }}
            css={{
              'abyss-text-input-label': {
                position: 'relative',
                fontSize: '15px',
              },
            }}
          />
        </FlexItem>

        <FlexItem>
          <SelectInput
            id="tinCount"
            label="How many Tax IDs (TINs) does your organization work with?"
            model="orgInfoPartOne.tinCount"
            placeholder="Choose an option"
            isClearable
            isSearchable
            validators={{ required: true }}
            options={[
              { label: '1', value: '1' },
              {
                label: '2-10',
                value: '2-10',
              },
              {
                label: '11-50',
                value: '11-50',
              },
              {
                label: '51-100',
                value: '51-100',
              },
              {
                label: '101-500',
                value: '101-500',
              },
              {
                label: '501–1000',
                value: '501–1000',
              },
              {
                label: '1001–5000',
                value: '1001–5000',
              },
              {
                label: '5001- 10000',
                value: '5001- 10000',
              },
              {
                label: '10001+',
                value: '10001+',
              },
            ]}
            css={{
              'abyss-select-input-label': {
                position: 'relative',
                fontSize: '15px',
              },
            }}
          />
        </FlexItem>
        <FlexItem>
          <SelectInput
            id="orgType"
            label="Organization Type"
            model="orgInfoPartOne.orgType"
            placeholder="Choose an option"
            isClearable
            isSearchable
            options={[
              { label: 'Provider', value: 'Provider' },
              {
                label: 'Revenue Cycle Management (RCM)',
                value: 'Revenue Cycle Management (RCM)',
              },
              { label: 'Clearinghouse', value: 'Clearinghouse' },
              {
                label: 'FQHC, RHC or Tribal Group',
                value: 'FQHC, RHC or Tribal Group',
              },
              { label: 'Other', value: 'Other' },
            ]}
            validators={{ required: true }}
            onChange={onOrgTypeChange}
            css={{
              'abyss-select-input-label': {
                position: 'relative',
                fontSize: '15px',
                marginBottom: '29px',
              },
            }}
          />
        </FlexItem>
      </FlexDisplay>
    </Wrapper>
  );
};
